import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarIcon, Users, Minus, Plus, Search } from "lucide-react";
import { format } from "date-fns";
import { it, enGB, fr, de, es } from "date-fns/locale";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useT } from "@/i18n/LanguageContext";
import { useBlockedDates, isDateBlocked } from "@/hooks/useBlockedDates";

const LOCALES = { it, en: enGB, fr, de, es };
const initialGuests = { adulti: 2, bambini: 0, neonati: 0 };

function readStoredWidget() {
  try {
    const stored = sessionStorage.getItem("booking-widget");
    if (!stored) return null;
    const p = JSON.parse(stored);
    return {
      checkIn: p.checkIn ? new Date(p.checkIn) : null,
      checkOut: p.checkOut ? new Date(p.checkOut) : null,
      guests: p.guests || null,
    };
  } catch (_e) {
    return null;
  }
}

export default function BookingWidget({ variant = "hero", apartmentSlug = "attico-skyline-milano" }) {
  const navigate = useNavigate();
  const { t, lang } = useT();
  const locale = LOCALES[lang] || it;
  const blocked = useBlockedDates();
  const stored = readStoredWidget();
  const [checkIn, setCheckIn] = useState(stored?.checkIn || null);
  const [checkOut, setCheckOut] = useState(stored?.checkOut || null);
  const [guests, setGuests] = useState(stored?.guests || initialGuests);
  const [openIn, setOpenIn] = useState(false);
  const [openOut, setOpenOut] = useState(false);
  const [openGuests, setOpenGuests] = useState(false);

  // First blocked date strictly after a given check-in date (used to cap check-out)
  const firstBlockedAfter = (start) => {
    if (!start) return null;
    const cur = new Date(start);
    cur.setDate(cur.getDate() + 1);
    for (let i = 0; i < 365; i++) {
      if (isDateBlocked(cur, blocked)) return new Date(cur);
      cur.setDate(cur.getDate() + 1);
    }
    return null;
  };

  const totalGuests = guests.adulti + guests.bambini;
  const guestSummary =
    totalGuests === 0
      ? t("bw.guests")
      : `${totalGuests} ${totalGuests === 1 ? t("bw.guest_one") : t("bw.guest_many")}${guests.neonati ? ` + ${guests.neonati}` : ""}`;

  const updateGuest = (key, delta) => {
    setGuests((g) => {
      const next = { ...g, [key]: Math.max(0, g[key] + delta) };
      if (key === "adulti") next.adulti = Math.max(1, next.adulti);
      return next;
    });
  };

  const handleSubmit = () => {
    if (!checkIn || !checkOut) return;
    const payload = {
      checkIn: checkIn.toISOString(),
      checkOut: checkOut.toISOString(),
      guests,
      apartmentSlug,
    };
    sessionStorage.setItem("booking-widget", JSON.stringify(payload));
    navigate("/prenotazione");
  };

  const containerClasses =
    variant === "hero"
      ? "glass shadow-[0_20px_60px_rgba(0,0,0,0.15)] border border-white/40 rounded-2xl p-3 md:p-2"
      : "bg-white border border-cream-200 shadow-[0_8px_30px_rgba(0,0,0,0.04)] rounded-2xl p-3 md:p-2";

  return (
    <div data-testid="booking-widget" className={containerClasses}>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-2 md:gap-0 md:divide-x md:divide-cream-200">
        {/* Check-in */}
        <Popover open={openIn} onOpenChange={setOpenIn}>
          <PopoverTrigger asChild>
            <button
              data-testid="bw-checkin-trigger"
              className="text-left rounded-xl md:rounded-l-2xl md:rounded-r-none px-5 py-3 hover:bg-cream-50/60 transition-colors"
            >
              <p className="text-[10px] uppercase tracking-[0.18em] text-navy-700 font-semibold">{t("bw.checkin")}</p>
              <p className="mt-1 text-sm font-medium text-slate-900 flex items-center gap-2">
                <CalendarIcon size={15} className="text-slate-600" />
                {checkIn ? format(checkIn, "d MMM yyyy", { locale }) : t("bw.add_date")}
              </p>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0 border-cream-200" align="start">
            <Calendar
              mode="single"
              selected={checkIn}
              onSelect={(d) => {
                if (!d) return;
                setCheckIn(d);
                if (checkOut && d >= checkOut) setCheckOut(null);
                setOpenIn(false);
                setTimeout(() => setOpenOut(true), 120);
              }}
              disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0)) || isDateBlocked(d, blocked)}
              locale={locale}
              initialFocus
            />
          </PopoverContent>
        </Popover>

        {/* Check-out */}
        <Popover open={openOut} onOpenChange={setOpenOut}>
          <PopoverTrigger asChild>
            <button
              data-testid="bw-checkout-trigger"
              className="text-left rounded-xl md:rounded-none px-5 py-3 hover:bg-cream-50/60 transition-colors"
            >
              <p className="text-[10px] uppercase tracking-[0.18em] text-navy-700 font-semibold">{t("bw.checkout")}</p>
              <p className="mt-1 text-sm font-medium text-slate-900 flex items-center gap-2">
                <CalendarIcon size={15} className="text-slate-600" />
                {checkOut ? format(checkOut, "d MMM yyyy", { locale }) : t("bw.add_date")}
              </p>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0 border-cream-200" align="start">
            <Calendar
              mode="single"
              selected={checkOut}
              onSelect={(d) => {
                if (!d) return;
                setCheckOut(d);
                setOpenOut(false);
              }}
              disabled={(d) => {
                const today = new Date(new Date().setHours(0, 0, 0, 0));
                if (d < today) return true;
                if (checkIn && d <= checkIn) return true;
                const cap = firstBlockedAfter(checkIn);
                if (cap && d > cap) return true;
                return isDateBlocked(d, blocked);
              }}
              locale={locale}
              initialFocus
            />
          </PopoverContent>
        </Popover>

        {/* Guests */}
        <Popover open={openGuests} onOpenChange={setOpenGuests}>
          <PopoverTrigger asChild>
            <button
              data-testid="bw-guests-trigger"
              className="text-left rounded-xl md:rounded-none px-5 py-3 hover:bg-cream-50/60 transition-colors"
            >
              <p className="text-[10px] uppercase tracking-[0.18em] text-navy-700 font-semibold">{t("bw.guests")}</p>
              <p className="mt-1 text-sm font-medium text-slate-900 flex items-center gap-2">
                <Users size={15} className="text-slate-600" />
                {guestSummary}
              </p>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-80 p-5 border-cream-200" align="start">
            {[
              { key: "adulti", label: t("bw.adults"), hint: t("bw.adults_hint") },
              { key: "bambini", label: t("bw.children"), hint: t("bw.children_hint") },
              { key: "neonati", label: t("bw.infants"), hint: t("bw.infants_hint") },
            ].map((row, i) => (
              <div key={row.key} className={`flex items-center justify-between ${i > 0 ? "mt-5" : ""}`}>
                <div>
                  <p className="font-medium text-slate-900 text-sm">{row.label}</p>
                  <p className="text-xs text-slate-600">{row.hint}</p>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    data-testid={`bw-guest-${row.key}-minus`}
                    onClick={() => updateGuest(row.key, -1)}
                    disabled={(row.key === "adulti" && guests.adulti <= 1) || (row.key !== "adulti" && guests[row.key] <= 0)}
                    className="w-8 h-8 rounded-full border border-cream-200 grid place-items-center text-slate-800 hover:border-slate-900 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <Minus size={14} />
                  </button>
                  <span data-testid={`bw-guest-${row.key}-value`} className="w-5 text-center text-sm font-medium text-slate-900">
                    {guests[row.key]}
                  </span>
                  <button
                    data-testid={`bw-guest-${row.key}-plus`}
                    onClick={() => updateGuest(row.key, 1)}
                    className="w-8 h-8 rounded-full border border-cream-200 grid place-items-center text-slate-800 hover:border-slate-900 transition-colors"
                  >
                    <Plus size={14} />
                  </button>
                </div>
              </div>
            ))}
          </PopoverContent>
        </Popover>

        {/* CTA */}
        <button
          data-testid="bw-submit"
          onClick={handleSubmit}
          disabled={!checkIn || !checkOut}
          className="flex items-center justify-center gap-2 bg-navy-600 hover:bg-navy-700 disabled:bg-navy-600/50 text-white rounded-xl md:rounded-l-none md:rounded-r-2xl px-5 py-4 md:py-3 font-medium text-sm transition-all hover:-translate-y-0.5 disabled:hover:translate-y-0"
        >
          <Search size={16} />
          <span>{t("bw.submit")}</span>
        </button>
      </div>
    </div>
  );
}
